import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import {
  SOUND_LABELS,
  SOUND_NAMES,
  type SoundName,
  playSound,
  unlockAudio,
} from '@/renderer/utils/play';

/**
 * "Test sounds" (#2041).
 *
 * Two jobs. First, it is a deliberate user gesture, so pressing it performs the
 * autoplay unlock — a user who has heard nothing all season can fix that here
 * instead of guessing. Second, it proves the tones out loud and, when one can't
 * play, says so inline rather than failing silently the way the release-center
 * path used to.
 *
 * Tones are played in sequence with a gap, so they're distinguishable rather
 * than one overlapping blur.
 */

const GAP_MS = 900;

// Only the Release Center tones — the support-chat chime isn't what anyone is
// checking when they come here.
const TEST_ORDER: SoundName[] = SOUND_NAMES.filter((name) => name !== 'pop');

type Result = { name: SoundName; ok: boolean };

export default function TestSounds() {
  const [running, setRunning] = useState(false);
  const [playing, setPlaying] = useState<SoundName | null>(null);
  const [results, setResults] = useState<Result[] | null>(null);
  const [unlockFailed, setUnlockFailed] = useState(false);

  const run = async () => {
    setRunning(true);
    setResults(null);
    setUnlockFailed(false);

    try {
      // Synchronously inside this click, before anything is awaited — Safari
      // only honors the unlock while the gesture is still live.
      const unlocked = await unlockAudio();
      setUnlockFailed(!unlocked);

      const collected: Result[] = [];

      for (const name of TEST_ORDER) {
        setPlaying(name);
        const ok = await playSound(name);
        collected.push({ name, ok });
        setResults([...collected]);
        await new Promise((resolve) => setTimeout(resolve, GAP_MS));
      }
    } finally {
      setPlaying(null);
      setRunning(false);
    }
  };

  const failed = results?.filter((result) => !result.ok) ?? [];
  const allOk = !!results && results.length === TEST_ORDER.length && failed.length === 0;

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={run}
        disabled={running}
        className="cursor-pointer rounded-md bg-emerald-600 px-3 py-2 font-semibold text-sm text-white shadow-xs hover:bg-emerald-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-700 focus-visible:outline-offset-2 disabled:cursor-progress disabled:opacity-60"
      >
        {running ? 'Playing…' : 'Test sounds'}
      </button>

      {running && playing && (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">Playing {SOUND_LABELS[playing]}…</p>
      )}

      {allOk && (
        <p className="flex items-center gap-1.5 text-emerald-700 text-sm dark:text-emerald-400">
          <CheckCircleIcon className="size-4 shrink-0" />
          All notification sounds played.
        </p>
      )}

      {!!results && failed.length > 0 && (
        <div className="space-y-1 text-sm">
          <p className="flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
            <ExclamationTriangleIcon className="size-4 shrink-0" />
            {failed.length === TEST_ORDER.length
              ? 'This device blocked notification sounds.'
              : `Couldn't play: ${failed.map((result) => SOUND_LABELS[result.name]).join(', ')}.`}
          </p>
          <p className="text-zinc-500 dark:text-zinc-400">
            {unlockFailed
              ? 'Check that the device is not muted or on silent, then try again. On iPhone, the ringer switch and Focus modes can both silence in-app sounds.'
              : 'Try again, or reload the page. If it keeps failing, let support know which device and browser you are on.'}
          </p>
        </div>
      )}
    </div>
  );
}
